import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, toDisplayString as _toDisplayString, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "consultation-drawer__close-trigger" }
const _hoisted_2 = { class: "consultation-drawer__form-container" }
const _hoisted_3 = { class: "consultation-drawer__row" }
const _hoisted_4 = { class: "consultation-drawer__contact-cards" }
const _hoisted_5 = { class: "consultation-drawer__contact-cards-item" }
const _hoisted_6 = { class: "consultation-drawer__contact-cards-item-icon" }
const _hoisted_7 = ["src"]
const _hoisted_8 = { class: "consultation-drawer__contact-cards-item" }
const _hoisted_9 = { class: "consultation-drawer__contact-cards-item-icon" }
const _hoisted_10 = ["src"]
const _hoisted_11 = { class: "consultation-drawer__toolbar" }
const _hoisted_12 = { class: "consultation-drawer__buttons-container" }
const _hoisted_13 = ["disabled"]

import { computed, nextTick, onMounted, reactive, ref } from 'vue';
import { CloseOutlined } from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import type { FormInstance, Rule } from 'ant-design-vue/es/form';

import FormFullNameField from '@/js/components/form/form-full-name-field.vue';
import FormPhoneField from '@/js/components/form/form-phone-field.vue';
import FormEmailField from '@/js/components/form/form-email-field.vue';
import FormSubjectField, {
  type FormSubjectOption,
} from '@/js/components/form/form-subject-field.vue';
import FormQuestionField from '@/js/components/form/form-question-field.vue';
import ConsultationRequestSuccessModal from '@/js/components/consultation-request-success-modal/consultation-request-success-modal.vue';
import { getApiUrl } from '@/js/utils/getApiUrl';
import { submitFormWithCaptcha } from '@/js/utils/submitFormWithCaptcha';
import { getRecaptchaErrorMessage } from '@/js/utils/getRecaptchaToken';
import FormPrivacyConsentField from '@/js/components/form/form-privacy-consent-field.vue';
import { trackYaGoal } from '@/js/utils/yandex-metrika';

interface ConsultationFormMetaItem {
  id: string;
  text: string;
}

interface ConsultationFormMetaResponse {
  success?: boolean;
  data?: {
    subject?: ConsultationFormMetaItem[];
    privacy?: ConsultationFormMetaItem[];
  };
  msg?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'contact-us-draver',
  setup(__props, { expose: __expose }) {

const ICONS_BASE =
  process.env.NODE_ENV === 'development'
    ? '/assets/images/icons'
    : '/local/templates/dist/assets/images/icons';

const subjectOptions = ref<FormSubjectOption[]>([]);
const privacyOptionId = ref<string>('');

const openModal = ref<boolean>(false);
const successModalDescription = ref('');
const isSubmitting = ref(false);

const showModal = () => {
  openModal.value = true;
};

const drawerFormRef = ref<FormInstance>();

const initialFormState = () => ({
  fullName: '',
  companyName: '',
  email: '',
  phone: '',
  subject: '',
  question: '',
  privacy: false,
});

const form = reactive(initialFormState());

const isNotBlankString = (value: string): boolean => value.trim().length > 0;
const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const isRequiredFieldsFilled = computed(
  () =>
    isNotBlankString(form.fullName) &&
    isNotBlankString(form.companyName) &&
    isNotBlankString(form.email) &&
    isNotBlankString(form.subject) &&
    isNotBlankString(form.question) &&
    form.privacy,
);

const hasValidationErrors = computed(() => !isValidEmail(form.email));
const isSubmitDisabled = computed(
  () => isSubmitting.value || !isRequiredFieldsFilled.value || hasValidationErrors.value,
);

const rules: Record<string, Rule[]> = {
  fullName: [{ required: true, message: 'Введите ваше полное имя' }],
  companyName: [{ required: true, message: 'Введите название компании' }],
  email: [{ required: true, message: 'Введите e-mail', type: 'email' }],
  subject: [{ required: true, message: 'Выберите тему обращения' }],
  question: [{ required: true, message: 'Опишите ваш вопрос' }],
};

const open = ref<boolean>(false);

const openDrawer = (): void => {
  open.value = true;
};

const fetchConsultationFormMeta = async () => {
  try {
    const response = await fetch(getApiUrl('/api/addConsultForm.php'), {
      method: 'GET',
      credentials: 'same-origin',
    });
    const result = (await response.json()) as ConsultationFormMetaResponse;

    if (!response.ok || result.success === false) {
      throw new Error(result.msg ?? 'Не удалось загрузить данные формы');
    }

    const subjectList = result.data?.subject ?? [];
    const privacyList = result.data?.privacy ?? [];

    subjectOptions.value = subjectList.map((item) => ({
      value: item.id,
      label: item.text,
    }));
    privacyOptionId.value = privacyList[0]?.id ?? '';
  } catch {
    message.error('Не удалось загрузить темы обращения');
  }
};

const resetFormState = () => {
  Object.assign(form, initialFormState());
  nextTick(() => {
    drawerFormRef.value?.clearValidate();
  });
};

const onClose = () => {
  open.value = false;
  resetFormState();
};

const onSubmit = async () => {
  if (!drawerFormRef.value) {
    return;
  }

  try {
    await drawerFormRef.value.validate();
  } catch {
    return;
  }

  if (form.privacy && !privacyOptionId.value) {
    message.error('Не удалось определить согласие на обработку данных');
    return;
  }

  isSubmitting.value = true;

  try {
    const submitResult = await submitFormWithCaptcha({
      endpoint: getApiUrl('/api/addConsultForm.php'),
      buildFormData: () => {
        const body = new FormData();
        body.append('email', form.email);
        body.append('phone', form.phone);
        body.append('fio', form.fullName);
        body.append('company_name', form.companyName);
        body.append('subject', form.subject);
        body.append('question', form.question);
        if (form.privacy && privacyOptionId.value) {
          body.append('privacy', privacyOptionId.value);
        }

        return body;
      },
    });

    if (!submitResult.ok || !submitResult.data) {
      if (submitResult.errorCode === 'captcha-not-loaded') {
        message.error(getRecaptchaErrorMessage('captcha-not-loaded'));
        return;
      }

      if (submitResult.errorCode === 'captcha-failed') {
        message.error(getRecaptchaErrorMessage('captcha-failed'));
        return;
      }

      message.error(submitResult.message ?? 'Не удалось отправить обращение');
      return;
    }

    successModalDescription.value = submitResult.data.msg ?? '';
    trackYaGoal('submitApplication');
    onClose();
    showModal();
  } catch {
    message.error('Ошибка соединения');
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(() => {
  void fetchConsultationFormMeta();
});

__expose({
  open: openDrawer,
});

return (_ctx: any,_cache: any) => {
  const _component_a_input = _resolveComponent("a-input")!
  const _component_a_form_item = _resolveComponent("a-form-item")!
  const _component_a_form = _resolveComponent("a-form")!
  const _component_a_divider = _resolveComponent("a-divider")!
  const _component_a_drawer = _resolveComponent("a-drawer")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_a_drawer, {
      open: open.value,
      class: "consultation-drawer",
      "root-class-name": "consultation-drawer",
      onClose: onClose
    }, {
      title: _withCtx(() => [...(_cache[9] || (_cache[9] = [
        _createElementVNode("div", { class: "consultation-drawer__header-shell" }, [
          _createElementVNode("div", { class: "consultation-drawer__header-overline" }, "Консультация"),
          _createElementVNode("div", { class: "consultation-drawer__header-lede" }, [
            _createElementVNode("h1", null, "Поможем, если у вас остались вопросы по оформлению заявок.")
          ])
        ], -1)
      ]))]),
      closeIcon: _withCtx(() => [
        _createElementVNode("span", _hoisted_1, [
          _createVNode(_unref(CloseOutlined))
        ])
      ]),
      footer: _withCtx(() => [
        _createElementVNode("div", _hoisted_11, [
          _createVNode(FormPrivacyConsentField, {
            "model-value": form.privacy,
            "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((form.privacy) = $event)),
            class: "consultation-drawer__footer-policy"
          }, null, 8, ["model-value"]),
          _createElementVNode("div", _hoisted_12, [
            _createElementVNode("button", {
              type: "button",
              class: "btn btn-secondary btn-medium btn-cancel",
              onClick: onClose
            }, " Отмена "),
            _createElementVNode("button", {
              type: "button",
              class: "btn btn-primary btn-medium",
              onClick: onSubmit,
              disabled: isSubmitDisabled.value
            }, _toDisplayString(isSubmitting.value ? 'Отправка…' : 'Отправить'), 9, _hoisted_13)
          ])
        ])
      ]),
      default: _withCtx(() => [
        _createVNode(_component_a_form, {
          ref_key: "drawerFormRef",
          ref: drawerFormRef,
          model: form,
          rules: rules,
          layout: "vertical"
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_component_a_form_item, {
                class: "form-full-name-field stc-ant-field-skin",
                label: "Компания",
                name: "companyName",
                required: ""
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_a_input, {
                    class: "form-full-name-field__input",
                    value: form.companyName,
                    "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((form.companyName) = $event)),
                    placeholder: "Название компании"
                  }, null, 8, ["value"])
                ]),
                _: 1
              }),
              _createVNode(FormFullNameField, {
                "model-value": form.fullName,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.fullName) = $event))
              }, null, 8, ["model-value"]),
              _createElementVNode("div", _hoisted_3, [
                _createVNode(FormPhoneField, {
                  "model-value": form.phone,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.phone) = $event)),
                  required: false
                }, null, 8, ["model-value"]),
                _createVNode(FormEmailField, {
                  "model-value": form.email,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form.email) = $event))
                }, null, 8, ["model-value"])
              ]),
              _createVNode(FormSubjectField, {
                "model-value": form.subject,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((form.subject) = $event)),
                options: subjectOptions.value
              }, null, 8, ["model-value", "options"]),
              _createVNode(FormQuestionField, {
                "model-value": form.question,
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((form.question) = $event))
              }, null, 8, ["model-value"])
            ])
          ]),
          _: 1
        }, 8, ["model"]),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("img", {
                src: `${_unref(ICONS_BASE)}/email.svg`,
                alt: ""
              }, null, 8, _hoisted_7)
            ]),
            _cache[10] || (_cache[10] = _createElementVNode("div", { class: "consultation-drawer__contact-cards-item-body" }, [
              _createElementVNode("span", { class: "consultation-drawer__contact-cards-item-label" }, "Через почту"),
              _createElementVNode("a", {
                class: "consultation-drawer__contact-cards-item-link",
                href: "mailto:info@sticmsu.ru"
              }, "info@sticmsu.ru")
            ], -1))
          ]),
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("div", _hoisted_9, [
              _createElementVNode("img", {
                src: `${_unref(ICONS_BASE)}/phone-2.svg`,
                alt: ""
              }, null, 8, _hoisted_10)
            ]),
            _cache[11] || (_cache[11] = _createElementVNode("div", { class: "consultation-drawer__contact-cards-item-body" }, [
              _createElementVNode("span", { class: "consultation-drawer__contact-cards-item-label" }, "По телефону"),
              _createElementVNode("a", {
                class: "consultation-drawer__contact-cards-item-link",
                href: "tel:8-495-939-20-15"
              }, "8-495-939-20-15")
            ], -1))
          ])
        ]),
        _createVNode(_component_a_divider, { class: "consultation-drawer__mobile-divider" }),
        _createVNode(FormPrivacyConsentField, {
          "model-value": form.privacy,
          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((form.privacy) = $event)),
          class: "consultation-drawer__mobile-policy"
        }, null, 8, ["model-value"])
      ]),
      _: 1
    }, 8, ["open"]),
    _createVNode(ConsultationRequestSuccessModal, {
      open: openModal.value,
      "onUpdate:open": _cache[8] || (_cache[8] = ($event: any) => ((openModal).value = $event)),
      description: successModalDescription.value
    }, null, 8, ["open", "description"])
  ], 64))
}
}

})